/**
 * Commercial exceptions — headless vocabulary and derivations shared by the
 * board page, the pricing-target settings page, and (later) stories/tests.
 * Styling stays in the shell; this file owns labels, ordering, and maths.
 */

import type { ExceptionCard } from "../data/commercial-exceptions-types";

/** Badge labels for the KNOWN exception rules. Rules are data (authored in
 * the alert-definition catalog), so an unknown kind falls back to the flag's
 * own title at the call site. */
export const FLAG_LABELS: Record<string, string> = {
  below_pricing_model: "Below target",
  high_value: "High value",
  low_margin: "Low margin",
  high_margin: "High margin",
  new_customer: "New customer",
  reactivated_customer: "Reactivated",
  zero_value: "Zero value",
};

export function flagLabel(flag: { kind: string; title: string }): string {
  return FLAG_LABELS[flag.kind] ?? flag.title;
}

/** Hours since a card was raised, against an injectable "now" for tests
 * and stories. Null when the card carries no raised_at. */
export function cardAgeHours(card: ExceptionCard, now: Date): number | null {
  if (!card.raised_at) return null;
  const raised = new Date(card.raised_at).getTime();
  return Math.max(0, (now.getTime() - raised) / 3_600_000);
}

export type AgeSeverity = "fresh" | "aging" | "overdue";

/** Aging model from the brief: amber past 4 working hours, red past 24. */
export function ageSeverity(hours: number): AgeSeverity {
  if (hours >= 24) return "overdue";
  if (hours >= 4) return "aging";
  return "fresh";
}

export function formatAge(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

/** Share of a recent-CM sample that would flag below the given target.
 * Returns null with no target or an empty sample. */
export function flagRateAtTarget(
  recentMargins: number[],
  target: number | null,
): number | null {
  if (target == null || recentMargins.length === 0) return null;
  const below = recentMargins.filter((m) => m < target).length;
  return below / recentMargins.length;
}

export function marginQuantiles(recentMargins: number[]): {
  p25: number;
  median: number;
  p75: number;
} | null {
  if (recentMargins.length === 0) return null;
  const sorted = [...recentMargins].sort((a, b) => a - b);
  const q = (p: number) => {
    const idx = (sorted.length - 1) * p;
    const lo = Math.floor(idx);
    const hi = Math.ceil(idx);
    return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
  };
  return { p25: q(0.25), median: q(0.5), p75: q(0.75) };
}

export function formatCmPct(fraction: number | null): string {
  if (fraction == null) return "—";
  return `${(fraction * 100).toFixed(1)}%`;
}
